/**
 * Shared Formatting Utilities
 *
 * Centralized formatters to eliminate duplication across webapp components.
 * Used by: Dashboard, Pipeline, DealDetails, ContactDetails, Contacts, Companies
 */

// ============================================================================
// Currency Formatting
// ============================================================================

/**
 * Format a currency value with symbol
 * Uses locale-aware formatting for consistency
 *
 * @param {number} value - The numeric value to format
 * @param {string} currency - Currency code (default: 'EUR')
 * @returns {string} Formatted currency string (e.g., "1.234 €" or "$1,234")
 */
export function formatCurrency(value, currency = 'EUR') {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format currency with full precision (includes cents)
 *
 * @param {number} value - The numeric value to format
 * @param {string} currency - Currency code (default: 'EUR')
 * @returns {string} Formatted currency string with decimals (e.g., "1.234,56 €")
 */
export function formatCurrencyPrecise(value, currency = 'EUR') {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a date for display (handles Firestore Timestamps, strings, and Date objects)
 *
 * @param {any} date - Date value (Firestore Timestamp, ISO string, or Date object)
 * @param {string} fallback - Fallback string if date is invalid (default: 'Not set')
 * @returns {string} Formatted date string (e.g., "Jan 15, 2025")
 */
export function formatDate(date, fallback = 'Not set') {
  if (!date) return fallback;

  try {
    let dateObj;

    // Handle Firestore Timestamp (has toDate method)
    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    }
    // Handle ISO string or timestamp number
    else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    }
    // Handle Date object
    else if (date instanceof Date) {
      dateObj = date;
    }
    else {
      return fallback;
    }

    // Validate the date
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('[formatDate] Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format a date with time
 *
 * @param {any} date - Date value
 * @param {string} fallback - Fallback string if date is invalid
 * @returns {string} Formatted date and time string (e.g., "Jan 15, 2025, 2:30 PM")
 */
export function formatDateTime(date, fallback = 'Not set') {
  if (!date) return fallback;

  try {
    let dateObj;

    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return fallback;
    }

    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('[formatDateTime] Error formatting date:', error);
    return 'Invalid date';
  }
}

// ============================================================================
// Initials
// ============================================================================

/**
 * Get initials from a name (first letter of first and last word)
 *
 * @param {string} name - Full name string
 * @returns {string} Two-character initials (e.g., "JD" for "John Doe")
 */
export function getInitials(name) {
  if (!name || typeof name !== 'string') return '??';

  const trimmed = name.trim();
  if (!trimmed) return '??';

  const words = trimmed.split(/\s+/);

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  // First letter of first word + first letter of last word
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// ============================================================================
// Age/Time Calculations
// ============================================================================

/**
 * Calculate days since a date (handles Firestore Timestamps)
 *
 * @param {any} createdAt - Creation date (Firestore Timestamp, ISO string, or number)
 * @returns {number} Number of days since the date (0 if invalid)
 */
export function daysSinceCreation(createdAt) {
  if (!createdAt) return 0;

  try {
    let date;

    // Handle Firestore Timestamp with toDate() method
    if (createdAt && typeof createdAt === 'object' && typeof createdAt.toDate === 'function') {
      date = createdAt.toDate();
    }
    // Handle serialized Firestore Timestamp { seconds: ..., nanoseconds: ... }
    else if (createdAt && typeof createdAt === 'object' && typeof createdAt.seconds === 'number') {
      date = new Date(createdAt.seconds * 1000);
    }
    // Handle ISO string or timestamp number
    else if (typeof createdAt === 'string' || typeof createdAt === 'number') {
      date = new Date(createdAt);
    } else {
      return 0;
    }

    if (isNaN(date.getTime())) return 0;

    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    return 0;
  }
}

/**
 * Get color based on age in days (for deal staleness indicators)
 *
 * @param {number} days - Number of days
 * @returns {string} Tailwind color class (gray < 7 days, amber < 14 days, red >= 14 days)
 */
export function getAgeColor(days) {
  if (days < 7) return 'text-neutral-400';    // Gray - normal
  if (days < 14) return 'text-amber-500';     // Amber - warning
  return 'text-red-500';                       // Red - alert
}

// ============================================================================
// Form Styling Constants
// ============================================================================

/**
 * Base CSS classes for form inputs
 * Used by FormField and CompanySuggestions components
 */
export const inputBaseClass = `
  w-full rounded-lg
  bg-gray-50 dark:bg-[#192633]
  border border-gray-300 dark:border-[#324d67]
  text-slate-900 dark:text-white
  placeholder-slate-400 dark:placeholder-[#92adc9]
  focus:ring-2 focus:ring-primary focus:border-transparent
  p-3 text-base transition-all
`.trim().replace(/\s+/g, ' ');
